import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (email, password) => api.post('/auth/register', { email, password });
export const changePassword = (currentPassword, newPassword) => 
  api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const requestPasswordReset = (email) => api.post('/auth/request-reset', { email });
export const confirmPasswordReset = (email, code, newPassword) => 
  api.post('/auth/confirm-reset', { email, code, new_password: newPassword });
export const getMe = () => api.get('/auth/me');

// Subjects
export const getSubjects = () => api.get('/subjects');

// Quiz
export const startQuiz = (subject) => api.get(`/quiz/start/${subject}`);
export const submitQuiz = (subject, answers) => api.post('/quiz/submit', { subject, answers });

// History & Stats
export const getHistory = () => api.get('/history');
export const getAttemptDetails = (attemptId) => api.get(`/history/${attemptId}`);
export const getStats = () => api.get('/stats');
export const explainQuestion = (data) => api.post('/ai/explain-question', data);
export const getStudyRecommendations = (forceRefresh = false) =>
  api.post('/ai/study-recommendations', { force_refresh: forceRefresh });

// Questions (Admin)
export const getQuestions = (subject) => api.get('/questions', { params: subject ? { subject } : {} });
export const getQuestion = (id) => api.get(`/questions/${id}`);
export const createQuestion = (data) => api.post('/questions', data);
export const updateQuestion = (id, data) => api.put(`/questions/${id}`, data);
export const deleteQuestion = (id) => api.delete(`/questions/${id}`);

// Admin - Users
export const getUsers = () => api.get('/admin/users');
export const updateUserAccess = (userId, remainingDays) =>
  api.put(`/admin/users/${userId}/access`, { remaining_days: remainingDays });
export const getUserHistory = (userId) => api.get(`/admin/users/${userId}/history`);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const toggleUserAdmin = (userId) => api.put(`/admin/users/${userId}/toggle-admin`);

// Admin - Question Reviews
export const getQuestionReviews = (status) =>
  api.get('/admin/question-reviews', { params: status && status !== 'all' ? { status } : {} });
export const deleteReviewedQuestion = (reviewId) =>
  api.delete(`/admin/question-reviews/${reviewId}/question`);
export const updateQuestionReviewStatus = (
  reviewId,
  status,
  adminNote = '',
  officialExplanation = null
) =>
  api.put(`/admin/question-reviews/${reviewId}`, {
    status,
    admin_note: adminNote,
    official_explanation: officialExplanation,
  });

// Admin - Allowed Emails
export const getAllowedEmails = () => api.get('/admin/allowed-emails');
export const addAllowedEmail = (email) => api.post('/admin/allowed-emails', { email });
export const deleteAllowedEmail = (email) => api.delete(`/admin/allowed-emails/${encodeURIComponent(email)}`);

// Admin - CSV Upload
export const uploadQuestions = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/upload-questions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadAllowedEmails = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/upload-allowed-emails', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export default api;
